import { useEffect, useId, useRef, useState } from "react";
import MarinePhysicsLayer from "../MarinePhysicsLayer";
import {
  makeMarinePhysicsModel,
  resolveMarineAngle,
  stepMarinePhysics,
  type MarinePhysicsModel,
} from "../marinePhysics";
import classes from "./SignInSide.module.css";

interface MarineJellyfishProps {
  interactive?: boolean;
  onActivate?: () => void;
  className?: string;
  animatePhysics?: boolean;
  physicsModel?: MarinePhysicsModel;
  renderPhysicsLayer?: boolean;
  rotateBody?: boolean;
}

export default function MarineJellyfish({
  interactive = false,
  onActivate,
  className = "",
  animatePhysics = true,
  physicsModel,
  renderPhysicsLayer = true,
  rotateBody = true,
}: MarineJellyfishProps) {
  const gradientId = `jellyfish-bell-${useId().replace(/:/g, "")}`;
  const internalModelRef = useRef<MarinePhysicsModel | null>(null);
  if (!internalModelRef.current) internalModelRef.current = makeMarinePhysicsModel("jellyfish");
  const [physicsTick, setPhysicsTick] = useState(0);

  useEffect(() => {
    if (!animatePhysics || physicsModel || typeof window === "undefined" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    let frame = 0;
    let previous = performance.now();
    const draw = (now: number) => {
      const elapsed = Math.min(0.06, Math.max(0, (now - previous) / 1000));
      previous = now;
      const model = internalModelRef.current;
      if (model) {
        const velocity = { x: 42, y: Math.sin(now / 1250) * 9 };
        model.angle = resolveMarineAngle(model.angle, velocity, elapsed);
        stepMarinePhysics(model, { x: 0, y: 0, angle: model.angle }, velocity, elapsed);
        setPhysicsTick((value) => value + 1);
      }
      frame = window.requestAnimationFrame(draw);
    };
    frame = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(frame);
  }, [animatePhysics, physicsModel]);

  void physicsTick;
  const activeModel = physicsModel ?? (animatePhysics ? internalModelRef.current : null);
  const content = (
    <div className={`${classes.jellyfish} ${className}`.trim()}>
      <svg viewBox="0 0 96 128" role="presentation">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.98" />
            <stop offset="48%" stopColor="currentColor" stopOpacity="0.68" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.28" />
          </linearGradient>
          <radialGradient id={`${gradientId}-glow`} cx="50%" cy="18%" r="70%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.82" />
            <stop offset="58%" stopColor="currentColor" stopOpacity="0.22" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </radialGradient>
        </defs>
        {activeModel && renderPhysicsLayer && <MarinePhysicsLayer model={activeModel} />}
        <g transform={activeModel && rotateBody ? `rotate(${activeModel.angle * 180 / Math.PI} 48 45)` : undefined}>
          <path
            className={classes.jellyfishBell}
            d="M12 45C12 23 26 8 48 8s36 15 36 37c-5 1-8 7-13 5-5-2-8-7-13-3-5 4-9 8-14 3-5-4-9-1-14 2-5 3-8-4-13-5Z"
            fill={`url(#${gradientId})`}
          />
          <path
            className={classes.jellyfishBellGlow}
            d="M17 42C18 23 30 12 48 10s30 13 31 32c-6-4-10-5-15-1-5 3-9 5-14 1-5-4-10-4-15 0-6 4-10 3-18 0Z"
            fill={`url(#${gradientId}-glow)`}
          />
          <g className={classes.jellyfishRibs} fill="none">
            <path d="M48 9c-1 12-2 24-1 38" />
            <path d="M41 10c-2 12-5 24-6 36" />
            <path d="M55 10c2 12 5 24 6 36" />
            <path d="M33 14c-3 10-8 20-10 29" />
            <path d="M63 14c3 10 8 20 10 29" />
            <path d="M26 20c-2 7-6 14-8 21" />
            <path d="M70 20c2 7 6 14 8 21" />
          </g>
          <path className={classes.jellyfishBellShine} d="M35 17c4-4 8-6 13-7-5 7-8 15-9 24" />
          <path className={classes.jellyfishBellEdge} d="M12 45c5 1 8 8 13 5 5-3 9-6 14-2 5 4 9 1 14-3 5-4 8 1 13 3 5 2 8-4 13-5" />
          <g className={classes.jellyfishBubbles}>
            <circle cx="29" cy="28" r="1" />
            <circle cx="57" cy="23" r="0.8" />
            <circle cx="67" cy="34" r="1.2" />
            <circle cx="43" cy="34" r="0.7" />
          </g>
          {!activeModel && <path className={`${classes.jellyfishTentacle} ${classes.tentacleOne}`} d="M25 45c-2 14 8 16 3 27s8 16 2 29" />}
          {!activeModel && <path className={`${classes.jellyfishTentacle} ${classes.tentacleTwo}`} d="M39 47c-3 13 7 16 2 28s7 16 1 30" />}
          {!activeModel && <path className={`${classes.jellyfishTentacle} ${classes.tentacleThree}`} d="M54 47c3 13-7 16-2 28s-7 16-1 30" />}
          {!activeModel && <path className={`${classes.jellyfishTentacle} ${classes.tentacleFour}`} d="M69 45c2 14-8 16-3 27s-8 16-2 29" />}
        </g>
      </svg>
    </div>
  );

  if (!interactive) return content;
  return (
    <button
      type="button"
      className={classes.jellyfishButton}
      aria-label="Descubrir Guardianes de Protea"
      onClick={onActivate}
    >
      {content}
    </button>
  );
}
